import { getTheme, mergeStyleSets } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIcon } from 'baseComponents';
import { IPActionButton } from 'baseComponents/Button/IPButton';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPText } from 'baseComponents/Text/IPText';
import HrefLink from 'components/Links/HrefLink';
import { isNetworkError } from 'components/Notification/ErrorMessageHelper';
import { getErrorMessageText } from 'components/Notification/ErrorMessageService';
import SaveToLocationDialog from 'components/OutputLocations/SaveToLocationDialog';
import { CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import {
  CopilotFileBlockPayload,
  CopilotFileUnavailableReason,
  fileBlockIconName,
  getCopilotFileUnavailableReason,
} from 'models/copilot/copilotFileBlock';
import { ReportSaveLocation, ReportSaveRequest } from 'models/library/reportSaveLocation';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetCopilotDocumentContent,
  apiGetCopilotFileSaveLocation,
  apiSaveCopilotFileToLibrary,
} from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { downloadBlob } from 'utils/download';
import { bytesToSize } from 'utils/string';

interface ICopilotFileCardProps {
  block: CopilotConversationContentBlock;
  file: CopilotFileBlockPayload;
  sessionId?: number;
}

// folderName is known only from a save in this view; a reloaded block names the category alone
type SavedTo = { linkId: number; listId: number; folderName?: string };

// No HTTP response at all (the API is down or the network is gone): the error carries no status code
const isServerUnreachable = (err: unknown): boolean => {
  const appError = AppError.fromApiError(err);

  return !/^\d+$/.test(appError.code ?? '') && isNetworkError(appError);
};

const getTitle = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');

  return dot > 0 ? fileName.substring(0, dot) : fileName;
};

// A file a Copilot tool or the model made: its title, name and size, Download and Save to library while the session
// keeps the bytes, and what became of it (saved to a category, expired, held back by the malware scan).
export const CopilotFileCard = (props: ICopilotFileCardProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['copilot', 'exceptions', 'translation']);
  const theme = getTheme();
  const { block, file, sessionId } = props;
  const [isDownloading, setIsDownloading] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState<CopilotFileUnavailableReason | undefined>(
    file.tempBlobName ? undefined : CopilotFileUnavailableReason.Expired,
  );
  // the server writes the saved link into the stored block; the card shows it at once
  const [savedTo, setSavedTo] = useState<SavedTo | undefined>(
    file.savedLinkId !== undefined && file.savedListId !== undefined
      ? { linkId: file.savedLinkId, listId: file.savedListId }
      : undefined,
  );
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // the default location of a file with an output purpose (PLAN-069); undefined: the dialog opens on the picker
  const [saveLocation, setSaveLocation] = useState<ReportSaveLocation | undefined>(undefined);
  const [reportsStorageOff, setReportsStorageOff] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  // A new version of a report replaces the block in place (PLAN-070): the same block id with a new blob, so the card
  // starts over from the new payload (not expired, not saved yet); the Save to library flow works on the new blob.
  useEffect(() => {
    setUnavailableReason(file.tempBlobName ? undefined : CopilotFileUnavailableReason.Expired);
    setSavedTo(
      file.savedLinkId !== undefined && file.savedListId !== undefined
        ? { linkId: file.savedLinkId, listId: file.savedListId }
        : undefined,
    );
    setShowSaveDialog(false);
  }, [file.tempBlobName, file.savedLinkId, file.savedListId]);

  const showSaveError = (err: unknown) => {
    // the library's own refusal (no write permission, a name clash, no default location) says what to do; anything
    // else is generic
    const appError = AppError.fromApiError(err);
    let message = appError.data ? getErrorMessageText(appError, t) : t('copilot:Chat.File.SaveFailed');
    if (isServerUnreachable(appError)) message = t('copilot:Chat.File.ServerUnreachable');
    appContext.showNotification(message, true, false, true);
  };

  // A file with an output purpose shows its default location first; without a default (none bound, or the reports
  // storage switched off) the dialog says why and opens on the picker. A file without a purpose opens on the picker.
  const openSaveDialog = async () => {
    setSaveLocation(undefined);
    setReportsStorageOff(false);
    if (file.outputPurpose !== undefined && sessionId && block.id) {
      setIsLoadingLocation(true);
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        setSaveLocation(await apiGetCopilotFileSaveLocation(sessionId, block.id, accessToken));
      } catch (err) {
        const code = err instanceof AppError ? err.data?.code : undefined;
        if (code === ControllerExceptionCodes.Code_Report_SharePointReportsDisabled) {
          setReportsStorageOff(true);
        } else if (code !== ControllerExceptionCodes.Code_OutputLocation_NotBound) {
          showSaveError(err);
        }
      } finally {
        setIsLoadingLocation(false);
      }
    }
    setShowSaveDialog(true);
  };

  const styles = mergeStyleSets({
    surface: {
      maxWidth: 640,
      border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
      borderRadius: 4,
      background: theme.palette.white,
      padding: '6px 10px',
    },
    icon: {
      color: theme.palette.themePrimary,
      fontSize: 24,
    },
    title: {
      color: theme.palette.neutralPrimary,
      fontWeight: 600,
      wordBreak: 'break-word',
    },
    detail: {
      color: theme.palette.neutralSecondary,
      wordBreak: 'break-word',
    },
  });

  const canReach = !!sessionId && !!block.id;
  const isExpired = unavailableReason === CopilotFileUnavailableReason.Expired;
  const isRefused = unavailableReason === CopilotFileUnavailableReason.ScanRefused;

  const download = async () => {
    if (!sessionId || !block.id) return;

    setIsDownloading(true);
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const blob = await apiGetCopilotDocumentContent(sessionId, block.id, accessToken);
      setUnavailableReason(undefined);
      downloadBlob(blob, file.fileName);
    } catch (err) {
      const reason = getCopilotFileUnavailableReason(err);
      if (reason) {
        setUnavailableReason(reason);
      } else {
        appContext.showNotification(
          isServerUnreachable(err) ? t('copilot:Chat.File.ServerUnreachable') : t('copilot:Chat.File.DownloadFailed'),
          true,
          false,
          true,
        );
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // No listId: the default location of the purpose. When the start folder of that location is gone (OUTPUTLOC0009)
  // the file is downloaded instead and the toast says where to fix the location.
  const saveToLibrary = async (listId: number | undefined, folderDriveItemId: string | undefined) => {
    if (!sessionId || !block.id) return;

    const request: ReportSaveRequest =
      listId !== undefined ? { listId, parentFolderId: folderDriveItemId } : { purpose: file.outputPurpose };

    setIsSaving(true);
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiSaveCopilotFileToLibrary(sessionId, block.id, request, accessToken);
      setSavedTo({ linkId: result.linkId, listId: result.listId, folderName: result.folderName });
      setShowSaveDialog(false);
    } catch (err) {
      const appError = AppError.fromApiError(err);
      if (appError.data?.code === ControllerExceptionCodes.Code_OutputLocation_StartFolderNotFound) {
        setShowSaveDialog(false);
        appContext.showNotification(t('copilot:Chat.File.LocationFolderGone'), false, true, true);
        await download();
      } else {
        showSaveError(appError);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderStatus = () => {
    if (isExpired) return <IPText className={styles.detail}>{t('copilot:Chat.File.Expired')}</IPText>;
    if (isRefused) return <IPText className={styles.detail}>{t('copilot:Chat.File.ScanRefused')}</IPText>;
    if (unavailableReason === CopilotFileUnavailableReason.ScanPending) {
      return <IPText className={styles.detail}>{t('copilot:Chat.File.ScanPending')}</IPText>;
    }
    // the file is reached through the conversation's session; until there is one the buttons cannot work, so say why
    if (!canReach) return <IPText className={styles.detail}>{t('copilot:Chat.File.NotReachable')}</IPText>;

    return null;
  };

  const renderSavedTo = () => {
    if (!savedTo) return null;

    const category = appContext.globalDataCache.lists.get(savedTo.listId)?.name ?? '';

    return (
      <HrefLink href={`/library/links?id=${savedTo.linkId}`}>
        {savedTo.folderName
          ? t('copilot:Chat.File.SavedToFolder', { category, folder: savedTo.folderName })
          : t('copilot:Chat.File.SavedTo', { category })}
      </HrefLink>
    );
  };

  return (
    <>
      <IPStack tokens={{ childrenGap: 6 }} className={styles.surface}>
        <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
          <IPStack.Item>
            <IPIcon iconName={fileBlockIconName(block.mimeType)} className={styles.icon} />
          </IPStack.Item>
          <IPStack.Item grow>
            <IPStack>
              <IPText className={styles.title}>{getTitle(file.fileName)}</IPText>
              <IPText variant="small" className={styles.detail}>
                {file.sizeBytes > 0 ? `${file.fileName} - ${bytesToSize(file.sizeBytes)}` : file.fileName}
              </IPText>
            </IPStack>
          </IPStack.Item>
        </IPStack>
        {renderStatus()}
        {renderSavedTo()}
        {!isExpired && (
          <IPStack horizontal wrap tokens={{ childrenGap: 4 }}>
            <IPActionButton
              iconProps={{ iconName: 'Download' }}
              text={t('copilot:Chat.File.Download')}
              disabled={isDownloading || isRefused || !canReach}
              onClick={() => void download()}
            />
            {/* a file a process stages decides its own fate (the supplier check saves or offers it on its card) */}
            {!savedTo && !file.managedByProcess && (
              <IPActionButton
                iconProps={{ iconName: 'SaveTo' }}
                text={t('copilot:Chat.File.SaveToLibrary')}
                disabled={isRefused || !canReach || isLoadingLocation}
                onClick={() => void openSaveDialog()}
              />
            )}
          </IPStack>
        )}
      </IPStack>
      {showSaveDialog && (
        <SaveToLocationDialog
          fileName={saveLocation?.fileName ?? file.fileName}
          location={saveLocation}
          purpose={file.outputPurpose}
          reportsStorageOff={reportsStorageOff}
          isSaving={isSaving}
          onSave={saveToLibrary}
          onClose={() => setShowSaveDialog(false)}
        />
      )}
    </>
  );
};
